// services/excelService.js
import * as XLSX from 'xlsx';

// Configuration
const STORAGE_KEY = 'medical_platform_data';
const DATA_FILES = {
  patients: 'Patients.xlsx',
  consultations: 'Consultations.xlsx',
  vitals: 'Constantes.xlsx',
  treatments: 'Traitements.xlsx',
  medicalHistory: 'Suivi.xlsx',
  observations: 'Observations.xlsx'
};

// Helper function to get current timestamp
const getCurrentTimestamp = () => {
  return new Date().toISOString();
};

// Helper function to generate unique ID
const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Helper function to get doctor name from localStorage
const getDoctorName = () => {
  return localStorage.getItem('doctorName') || 'Dr. Non défini';
};

// Initialize data structure in localStorage if not exists
const initializeData = () => {
  const existingData = localStorage.getItem(STORAGE_KEY);
  if (!existingData) {
    const initialData = {
      patients: [],
      consultations: [],
      vitals: [],
      treatments: [],
      medicalHistory: [],
      observations: []
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData));
  }
};

// Get data from localStorage
const getData = (dataType) => {
  initializeData();
  const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
  const result = data[dataType];
  
  // Vérifier que le résultat est un tableau
  if (!Array.isArray(result)) {
    console.warn(`⚠️ ${dataType} n'est pas un tableau, initialisation...`);
    const correctedData = { ...data, [dataType]: [] };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(correctedData));
    return [];
  }
  
  return result;
};

// Save data to localStorage
const saveData = (dataType, data) => {
  const allData = JSON.parse(localStorage.getItem(STORAGE_KEY));
  allData[dataType] = data;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allData));
};

// Export data to Excel file
const exportToExcel = (data, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
  
  // Trigger download
  XLSX.writeFile(workbook, filename);
};

// Import data from Excel file
const importFromExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        resolve(jsonData);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// PATIENTS OPERATIONS
export const readPatients = () => {
  return getData('patients');
};

export const addPatient = (patientData) => {
  const patients = getData('patients');
  const newPatient = {
    id: generateId(),
    ...patientData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  patients.push(newPatient);
  saveData('patients', patients);
  return newPatient;
};

export const updatePatient = (id, updatedFields) => {
  const patients = getData('patients');
  const index = patients.findIndex(p => p.id === id);
  if (index !== -1) {
    patients[index] = {
      ...patients[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('patients', patients);
    return patients[index];
  }
  throw new Error('Patient not found');
};

export const deletePatient = (id) => {
  const patients = getData('patients');
  const filteredPatients = patients.filter(p => p.id !== id);
  saveData('patients', filteredPatients);
  return true;
};

// CONSULTATIONS OPERATIONS
export const readConsultations = () => {
  return getData('consultations');
};

export const addConsultation = (consultationData) => {
  const consultations = getData('consultations');
  const newConsultation = {
    id: generateId(),
    ...consultationData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  console.log('🔍 Ajout consultation:', newConsultation);
  consultations.push(newConsultation);
  saveData('consultations', consultations);
  return newConsultation;
};

export const updateConsultation = (id, updatedFields) => {
  const consultations = getData('consultations');
  const index = consultations.findIndex(c => c.id === id);
  if (index !== -1) {
    consultations[index] = {
      ...consultations[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('consultations', consultations);
    return consultations[index];
  }
  throw new Error('Consultation not found');
};

export const deleteConsultation = (id) => {
  const consultations = getData('consultations');
  const filteredConsultations = consultations.filter(c => c.id !== id);
  saveData('consultations', filteredConsultations);
  return true;
};

// VITALS OPERATIONS
export const readVitals = () => {
  return getData('vitals');
};

export const addVitalsData = (vitalsData) => {
  const vitals = getData('vitals');
  const newVitals = {
    id: generateId(),
    ...vitalsData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  console.log('🔍 Ajout constantes vitales:', newVitals);
  vitals.push(newVitals);
  saveData('vitals', vitals);
  return newVitals;
};

export const updateVitalsData = (id, updatedFields) => {
  const vitals = getData('vitals');
  const index = vitals.findIndex(v => v.id === id);
  if (index !== -1) {
    vitals[index] = {
      ...vitals[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('vitals', vitals);
    return vitals[index];
  }
  throw new Error('Vitals not found');
};

export const deleteVitalsData = (id) => {
  const vitals = getData('vitals');
  const filteredVitals = vitals.filter(v => v.id !== id);
  saveData('vitals', filteredVitals);
  return true;
};

// TREATMENTS OPERATIONS
export const readTreatments = () => {
  return getData('treatments');
};

export const addTreatment = (treatmentData) => {
  const treatments = getData('treatments');
  const newTreatment = {
    id: generateId(),
    ...treatmentData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  console.log('🔍 Ajout traitement:', newTreatment);
  treatments.push(newTreatment);
  saveData('treatments', treatments);
  return newTreatment;
};

export const updateTreatment = (id, updatedFields) => {
  const treatments = getData('treatments');
  const index = treatments.findIndex(t => t.id === id);
  if (index !== -1) {
    treatments[index] = {
      ...treatments[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('treatments', treatments);
    return treatments[index];
  }
  throw new Error('Treatment not found');
};

export const deleteTreatment = (id) => {
  const treatments = getData('treatments');
  const filteredTreatments = treatments.filter(t => t.id !== id);
  saveData('treatments', filteredTreatments);
  return true;
};

// MEDICAL HISTORY OPERATIONS
export const readMedicalHistory = () => {
  return getData('medicalHistory');
};

export const addMedicalHistory = (historyData) => {
  const medicalHistory = getData('medicalHistory');
  const newHistory = {
    id: generateId(),
    ...historyData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  medicalHistory.push(newHistory);
  saveData('medicalHistory', medicalHistory);
  return newHistory;
};

export const updateMedicalHistory = (id, updatedFields) => {
  const medicalHistory = getData('medicalHistory');
  const index = medicalHistory.findIndex(h => h.id === id);
  if (index !== -1) {
    medicalHistory[index] = {
      ...medicalHistory[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('medicalHistory', medicalHistory);
    return medicalHistory[index];
  }
  throw new Error('Medical history not found');
};

export const deleteMedicalHistory = (id) => {
  const medicalHistory = getData('medicalHistory');
  const filteredHistory = medicalHistory.filter(h => h.id !== id);
  saveData('medicalHistory', filteredHistory);
  return true;
};

// OBSERVATIONS OPERATIONS
export const readObservations = () => {
  return getData('observations');
};

export const addObservation = (observationData) => {
  const observations = getData('observations');
  const newObservation = {
    id: generateId(),
    ...observationData,
    createdBy: getDoctorName(),
    createdAt: getCurrentTimestamp(),
    updatedBy: getDoctorName(),
    updatedAt: getCurrentTimestamp()
  };
  observations.push(newObservation);
  saveData('observations', observations);
  return newObservation;
};

export const updateObservation = (id, updatedFields) => {
  const observations = getData('observations');
  const index = observations.findIndex(o => o.id === id);
  if (index !== -1) {
    observations[index] = {
      ...observations[index],
      ...updatedFields,
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    };
    saveData('observations', observations);
    return observations[index];
  }
  throw new Error('Observation not found');
};

export const deleteObservation = (id) => {
  const observations = getData('observations');
  const filteredObservations = observations.filter(o => o.id !== id);
  saveData('observations', filteredObservations);
  return true;
};

// EXPORT/IMPORT OPERATIONS
export const exportAllData = () => {
  const allData = JSON.parse(localStorage.getItem(STORAGE_KEY));
  
  Object.keys(DATA_FILES).forEach(dataType => {
    if (allData[dataType] && allData[dataType].length > 0) {
      exportToExcel(allData[dataType], DATA_FILES[dataType]);
    }
  });
};

export const importData = async (file, dataType) => {
  try {
    const importedData = await importFromExcel(file);
    const existingData = getData(dataType);
    
    // Add metadata to imported data
    const processedData = importedData.map(item => ({
      ...item,
      id: item.id || generateId(),
      createdBy: item.createdBy || getDoctorName(),
      createdAt: item.createdAt || getCurrentTimestamp(),
      updatedBy: getDoctorName(),
      updatedAt: getCurrentTimestamp()
    }));
    
    // Merge with existing data (avoid duplicates based on ID)
    const mergedData = [...existingData];
    processedData.forEach(newItem => {
      const existingIndex = mergedData.findIndex(item => item.id === newItem.id);
      if (existingIndex !== -1) {
        mergedData[existingIndex] = newItem;
      } else {
        mergedData.push(newItem);
      }
    });
    
    saveData(dataType, mergedData);
    return mergedData;
  } catch (error) {
    throw new Error(`Erreur lors de l'import: ${error.message}`);
  }
};

// Initialize the service
initializeData();
